/**
 * "Something changed here": one place made a change, and lists elsewhere on the page that
 * show it read again, without either knowing where the other is. "Elsewhere" includes the
 * split view's other pane, which is this app in a same-origin frame (WA-10), and the app's
 * other tabs: in a browser the signal also travels on a `BroadcastChannel` of its name.
 */
export interface ChangeSignal {
  readonly notify: () => void;
  /** Returns the unsubscribe function, for a `useEffect` cleanup. */
  readonly subscribe: (listener: () => void) => () => void;
}

export function createChangeSignal(name: string): ChangeSignal {
  const listeners = new Set<() => void>();
  const notifyHere = () => {
    for (const listener of listeners) listener();
  };
  // A channel never delivers to its own sender, so a change made here is announced once.
  const channel = typeof window !== "undefined" && "BroadcastChannel" in window ? new BroadcastChannel(`dmw.changed.${name}`) : null;
  channel?.addEventListener("message", notifyHere);
  return {
    notify: () => {
      notifyHere();
      channel?.postMessage(null);
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
